// eslint-disable-next-line no-unused-vars
const analyticsTypes = require('../jsdoc-types');
const {
  HashmapStorageFactory,
  HASHMAP_STORAGE_TYPE,
} = require('../../common/utils/hashmapStorage');
const {
  isVariationGroupId,
  isVariationProductId,
} = require('../../common/helpers/productIdHelpers');
const { isDoubleLook } = require('../../common/utils/doubleLookUtils');

/**
 * @typedef {Object} ItemListAndPosition
 * @property {string} list - The list content as a string.
 * @property {number} position - The current index in the list.
 */

/**
 * Creates an object containing a string list and a selected index.
 *
 * @param {string} list - A string representing the list of a product.
 * @param {number} position - The one-based position of the product in that list.
 * @returns {ItemListAndPosition} Object with list and position for analytics tracking.
 */
function createItemListAndPosition(list, position) {
  return { list, position };
}

const impressionItemListStorage = HashmapStorageFactory.createHashmapStorage({
  type: HASHMAP_STORAGE_TYPE.SESSION_STORAGE,
  name: 'ga.itemList.impressions',
  validateKey: (key) => {
    if (key !== 'current' && !isVariationGroupId(key) && !isDoubleLook(key)) {
      throw new TypeError(
        `Illegal argument: "${key}" is not a variation group ID `,
      );
    }
  },
});
const cartItemListStorage = HashmapStorageFactory.createHashmapStorage({
  type: HASHMAP_STORAGE_TYPE.LOCAL_STORAGE,
  name: 'ga.itemList.cart',
  validateItem: (item) => {
    const isValid = 'list' in item && 'position' in item;
    if (!isValid) {
      throw new TypeError('Illegal argument: item is not a ItemListAndPosition');
    }
  },
  validateKey: (key) => {
    if (!isVariationProductId(key)) {
      throw new TypeError(`Illegal argument: "${key}" is not a variant ID `);
    }
  },
});

/**
 * Returns the currently used list.
 * @returns {string|null} the current list
 *
 * @todo Review the purpose of this list: current page, last viewed list...
 */
function getCurrentList() {
  return impressionItemListStorage.getItem('current');
}

/**
 * Saves the currently used list.
 * @returns {string} the list to set as current
 * @returns {string|null} the saved list
 *
 * @todo Review the purpose of this list: current page, last viewed list...
 */
function setCurrentList(value) {
  return impressionItemListStorage.setItem('current', value);
}

/**
 * Get the list name from sessionStorage
 * @param {string} [variationGroupId] variation group ID
 * @returns {string} sessionStorage list name
 */
function getItemListForProductID(variationGroupId) {
  let list;
  if (variationGroupId) {
    list = impressionItemListStorage.getItem(variationGroupId);
  }
  return list || getCurrentList();
}

module.exports = {
  cartItemListStorage,
  createItemListAndPosition,
  getItemListForProductID,
  impressionItemListStorage,
  setCurrentList,
};
